import React, { useState, useEffect } from 'react';
import { Plus, Trash2, MapPin, Flag, CheckSquare, X, ChevronDown, ChevronUp, Edit2, Download, Upload } from 'lucide-react';
import { useStore } from '../store';
import { routeManager } from '../lib/routeManager';
import type { Route, Checkpoint } from '../types';

const ROUTE_COLORS = [
  '#FF0000', // 赤
  '#0000FF', // 青
  '#FFD700', // 黄
  '#32CD32', // 緑
  '#FF1493', // ピンク
  '#9400D3', // 紫
];

export const RouteEditor: React.FC = () => {
  const { 
    routes, 
    setRoutes, 
    activeRouteId, 
    setActiveRoute,
    isAddingPoint,
    pointType,
    setIsAddingPoint,
    setPointType
  } = useStore();

  const [editingRoute, setEditingRoute] = useState<Route | null>(null);
  const [editingCheckpoint, setEditingCheckpoint] = useState<Checkpoint | null>(null);
  const [editingCoordinates, setEditingCoordinates] = useState<{lng: string, lat: string} | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [routeNames, setRouteNames] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const unsubscribe = routeManager.subscribe(loadedRoutes => {
      setRoutes(loadedRoutes);
      // 初期のルート名をセット
      const names: { [key: string]: string } = {};
      loadedRoutes.forEach(route => {
        names[route.id] = route.name;
      });
      setRouteNames(names);
    });
    return () => unsubscribe();
  }, [setRoutes]);

  const handleAddRoute = async () => {
    if (isProcessing) return;
    if (routes.length >= 6) {
      alert('コースは最大6つまでです');
      return;
    }

    setIsProcessing(true);
    try {
      const newRoute: Route = {
        id: `route-${crypto.randomUUID()}`,
        name: `コース${routes.length + 1}`,
        color: ROUTE_COLORS[routes.length],
        coordinates: [],
        checkpoints: [],
        totalDistance: 0
      };

      await routeManager.saveRoute(newRoute);
      setEditingRoute(newRoute);
      setActiveRoute(newRoute.id);
      setRouteNames(prev => ({ ...prev, [newRoute.id]: newRoute.name }));
    } catch (error) {
      console.error('Error adding route:', error);
      alert('コースの作成に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditRoute = (route: Route) => {
    if (editingRoute?.id === route.id) {
      setEditingRoute(null);
      setActiveRoute(null);
      setIsAddingPoint(false);
    } else {
      setEditingRoute(route);
      setActiveRoute(route.id);
      setRouteNames(prev => ({ ...prev, [route.id]: route.name }));
    }
  };

  const handleDeleteRoute = async (id: string) => {
    if (isProcessing) return;
    if (window.confirm('このコースを削除してもよろしいですか？')) {
      setIsProcessing(true);
      try {
        const route = routes.find(r => r.id === id);
        if (route) {
          for (const checkpoint of route.checkpoints) {
            await routeManager.removeCheckpoint(id, checkpoint.id);
          }
        }
        await routeManager.deleteRoute(id);
        setEditingRoute(null);
        setActiveRoute(null);
        setIsAddingPoint(false);
        setRouteNames(prev => {
          const newNames = { ...prev };
          delete newNames[id];
          return newNames;
        });
      } catch (error) {
        console.error('Error deleting route:', error);
        alert('コースの削除に失敗しました');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleUpdateRouteName = async (route: Route) => {
    if (isProcessing) return;
    const newName = routeNames[route.id];
    if (!newName || newName === route.name) return;

    setIsProcessing(true);
    try {
      const updatedRoute = { ...route, name: newName };
      await routeManager.saveRoute(updatedRoute);
    } catch (error) {
      console.error('Error updating route name:', error);
      alert('コース名の更新に失敗しました');
      // エラー時は元の名前に戻す
      setRouteNames(prev => ({ ...prev, [route.id]: route.name }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePointTypeSelect = (type: 'start' | 'checkpoint' | 'goal') => {
    const activeRoute = routes.find(r => r.id === activeRouteId);
    
    if (type === 'start' && activeRoute?.checkpoints.some(cp => cp.type === 'start')) {
      return;
    }
    
    if (type === 'goal' && activeRoute?.checkpoints.some(cp => cp.type === 'goal')) {
      return;
    }
    
    setPointType(type);
    setIsAddingPoint(true);
  };

  const handleEditCheckpoint = (checkpoint: Checkpoint) => {
    setEditingCheckpoint(checkpoint);
    setEditingCoordinates({
      lng: checkpoint.position[0].toString(),
      lat: checkpoint.position[1].toString()
    });
  };

  const handleSaveCheckpoint = async (routeId: string, checkpoint: Checkpoint) => {
    if (!editingCheckpoint || !editingCoordinates || isProcessing) return;

    const lng = parseFloat(editingCoordinates.lng);
    const lat = parseFloat(editingCoordinates.lat);

    if (isNaN(lng) || isNaN(lat)) {
      alert('座標の形式が正しくありません');
      return;
    }

    setIsProcessing(true);
    try {
      const updatedCheckpoint = {
        ...checkpoint,
        position: [lng, lat] as [number, number]
      };

      await routeManager.updateCheckpoint(routeId, updatedCheckpoint);
      setEditingCheckpoint(null);
      setEditingCoordinates(null);
    } catch (error) {
      console.error('Error saving checkpoint:', error);
      alert('チェックポイントの保存に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveCheckpoint = async (routeId: string, checkpointId: string) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await routeManager.removeCheckpoint(routeId, checkpointId);
      const updatedRoute = routes.find(r => r.id === routeId);
      if (updatedRoute) {
        setEditingRoute({ ...updatedRoute });
      }
    } catch (error) {
      console.error('Error removing checkpoint:', error);
      alert('チェックポイントの削除に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">コース一覧</h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const routesData = JSON.stringify(routes, null, 2);
              const blob = new Blob([routesData], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `marathon-routes-${new Date().toISOString().split('T')[0]}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            className="p-2 text-gray-600 hover:text-green-500 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            disabled={routes.length === 0}
            title="エクスポート"
          >
            <Download className="w-5 h-5" />
          </button>
          
          <label className="p-2 text-gray-600 hover:text-blue-500 cursor-pointer transition-colors" title="インポート">
            <Upload className="w-5 h-5" />
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                try {
                  const text = await file.text();
                  const importedRoutes = JSON.parse(text);
                  
                  if (!Array.isArray(importedRoutes)) {
                    throw new Error('Invalid route data format');
                  }

                  // Validate and import each route
                  for (const route of importedRoutes) {
                    await routeManager.saveRoute(route);
                  }

                  alert('ルートのインポートが完了しました');
                } catch (error) {
                  console.error('Error importing routes:', error);
                  alert('ルートのインポートに失敗しました');
                }
              }}
            />
          </label>

          <button
            onClick={handleAddRoute}
            disabled={routes.length >= 6 || isProcessing}
            className="p-2 text-gray-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            title="新規コース"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {routes.map((route) => (
          <div key={route.id}>
            <div
              className={`flex items-center justify-between p-3 bg-white rounded-lg shadow cursor-pointer transition-colors ${
                route.id === activeRouteId ? 'ring-2 ring-blue-500' : 'hover:bg-gray-50'
              }`}
              onClick={() => handleEditRoute(route)}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: route.color }}
                />
                <span>{route.name}</span>
                {route.totalDistance > 0 && (
                  <span className="text-sm text-gray-500">
                    ({route.totalDistance.toFixed(3)}km)
                  </span>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRoute(route.id);
                  }}
                  disabled={isProcessing}
                  className="p-1 text-gray-600 hover:text-red-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {route.id === activeRouteId ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>
            </div>

            {editingRoute?.id === route.id && (
              <div className="mt-4 p-4 bg-white rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold">コース編集: {editingRoute.name}</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePointTypeSelect('start')}
                      className={`p-2 rounded-md transition-colors ${
                        isAddingPoint && pointType === 'start' ? 'bg-green-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      disabled={editingRoute.checkpoints.some(cp => cp.type === 'start')}
                      title="スタート地点"
                    >
                      <MapPin className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePointTypeSelect('checkpoint')}
                      className={`p-2 rounded-md transition-colors ${
                        isAddingPoint && pointType === 'checkpoint' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      title="チェックポイント"
                    >
                      <CheckSquare className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePointTypeSelect('goal')}
                      className={`p-2 rounded-md transition-colors ${
                        isAddingPoint && pointType === 'goal' ? 'bg-red-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      disabled={editingRoute.checkpoints.some(cp => cp.type === 'goal')}
                      title="ゴール地点"
                    >
                      <Flag className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    コース名
                  </label>
                  <input
                    type="text"
                    value={routeNames[route.id] || ''}
                    onChange={(e) => {
                      setRouteNames(prev => ({
                        ...prev,
                        [route.id]: e.target.value
                      }));
                    }}
                    onBlur={() => handleUpdateRouteName(route)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="mt-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">チェックポイント</h5>
                  <div className="space-y-2">
                    {editingRoute.checkpoints.map((checkpoint) => (
                      <div
                        key={checkpoint.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                      >
                        {editingCheckpoint?.id === checkpoint.id ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={editingCheckpoint.name}
                              onChange={(e) => setEditingCheckpoint({
                                ...editingCheckpoint,
                                name: e.target.value
                              })}
                              disabled={isProcessing}
                              className="flex-1 px-2 py-1 border rounded disabled:bg-gray-100"
                              placeholder="チェックポイント名"
                            />
                            <input
                              type="text"
                              value={editingCoordinates?.lng}
                              onChange={(e) => setEditingCoordinates({
                                ...editingCoordinates!,
                                lng: e.target.value
                              })}
                              disabled={isProcessing}
                              className="w-32 px-2 py-1 border rounded disabled:bg-gray-100"
                              placeholder="経度"
                            />
                            <input
                              type="text"
                              value={editingCoordinates?.lat}
                              onChange={(e) => setEditingCoordinates({
                                ...editingCoordinates!,
                                lat: e.target.value
                              })}
                              disabled={isProcessing}
                              className="w-32 px-2 py-1 border rounded disabled:bg-gray-100"
                              placeholder="緯度"
                            />
                            <button
                              onClick={() => handleSaveCheckpoint(route.id, editingCheckpoint)}
                              disabled={isProcessing}
                              className="p-1 text-green-600 hover:text-green-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <CheckSquare className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingCheckpoint(null);
                                setEditingCoordinates(null);
                              }}
                              disabled={isProcessing}
                              className="p-1 text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              {checkpoint.type === 'start' ? (
                                <MapPin className="w-4 h-4 text-green-500" />
                              ) : checkpoint.type === 'goal' ? (
                                <Flag className="w-4 h-4 text-red-500" />
                              ) : (
                                <CheckSquare className="w-4 h-4 text-blue-500" />
                              )}
                              <span>{checkpoint.name}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-sm text-gray-500">
                                {checkpoint.position[0].toFixed(6)}, {checkpoint.position[1].toFixed(6)}
                              </div>
                              <button
                                onClick={() => handleEditCheckpoint(checkpoint)}
                                disabled={isProcessing}
                                className="p-1 text-gray-400 hover:text-blue-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRemoveCheckpoint(editingRoute.id, checkpoint.id)}
                                disabled={isProcessing}
                                className="p-1 text-gray-400 hover:text-red-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};